import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Alert,
  Badge,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  InputGroup,
  Input,
  Modal,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import FeatherIcon from 'feather-icons-react';
import api from '../helpers/api';
import { translateActivity } from '../helpers/activityTranslator';
import TransferDetailsModal from '../components/planning/TransferDetailsModal';
import LocationModal from '../components/LocationModal';
import {
  formatCreatedDate,
  formatRoleName,
  formatEnumName,
  formatDateTimeForAPI,
} from '../constants/utils';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import usePermission from '../Hooks/usePermission';
import { COST_REPORT_VIEW, ASSET_TRANSFER, PROCUREMENT_REQUEST_CREATE } from '../constants/permissions';
import AssetStatusChangeModal from '../components/AssetStatusChangeModal';
import AssetTransferModal from '../components/AssetTransferModal';
import MeterDetailModal from './MeterDetail';
import TotalDowntimeWidget from './Reporting/TotalDowntimeWidget';
import DowntimeCostWidget from './Reporting/DowntimeCostWidget';
import AssetCostRollupCard from '../components/AssetCostRollupCard';
import AssetTCOPanel from '../components/AssetTCOPanel';
import { useTranslation } from 'react-i18next';

/**
 * Asset Detail page for deep links
 * Fetches asset by UUID and displays details or prompts to create/attach
 */
const METER_STATUS_COLORS = {
  PENDING: 'secondary',
  STABLE: 'success',
  WARNING: 'warning',
  ALARM: 'danger',
  OVERDUE: 'info',
};

const AssetDetail = () => {
  const { t } = useTranslation();
  const { isRequestor, hasPermission } = usePermission();
  const canViewCostReport = hasPermission(COST_REPORT_VIEW);
  const canTransferAsset = hasPermission(ASSET_TRANSFER);
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asset, setAsset] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [transferDetails, setTransferDetails] = useState({ isOpen: false, transfer: null });
  const [transferByProcess, setTransferByProcess] = useState({}); // process id -> parent transfer request
  const [planningLocations, setPlanningLocations] = useState([]);
  const [planningLocationModal, setPlanningLocationModal] = useState({ isOpen: false, title: '', mainData: null, relatedData: [], loading: false, locationData: null });

  // Pre-fetch the parent transfer requests (for a clickable Request ID + full modal data) and org
  // locations (for the location modal), so the Planning table matches the main Planning page.
  useEffect(() => {
    if (!asset?.equipmentProcesses?.length) return undefined;
    const orgId = asset?.organization?.id
      ?? JSON.parse(localStorage.getItem('authUser') || '{}')?.activeOrganizationId
      ?? null;
    if (!orgId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('equipment/getAllEquipmentRequests', {
          params: { requestType: 'TRANSFER', count: 1000, organizationId: orgId },
        });
        if (!cancelled) {
          const map = {};
          (data?.response || []).forEach((r) => (r.processInfo || []).forEach((p) => { map[p.id] = r; }));
          setTransferByProcess(map);
        }
      } catch (e) { /* ignore */ }
      try {
        const { data } = await api.get(`organization-location/get-by-organization-id?organizationId=${orgId}&count=9999&hierarchy=false`);
        if (!cancelled) setPlanningLocations(data?.response?.[0]?.locations || []);
      } catch (e) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [asset?.id, asset?.equipmentProcesses?.length, asset?.organization?.id]);

  // Open the shared transfer-details modal (same as the Planning page) for a Planning row.
  const openTransferDetails = useCallback((issue) => {
    const parent = transferByProcess[issue.id];
    const synthetic = {
      status: issue.status,
      requestDate: issue.createdAt || issue.startDate,
      processInfo: [{
        id: issue.id,
        linkedAsset: issue.linkedAsset || { id: asset?.id, name: asset?.assetName },
        fromLocationsInfo: issue.fromLocationsInfo,
        locationsInfo: issue.locationsInfo,
        startDate: issue.startDate,
        endDate: issue.endDate,
        reason: issue.reason,
        status: issue.status,
      }],
    };
    setTransferDetails({ isOpen: true, transfer: parent || synthetic });
  }, [transferByProcess, asset?.id, asset?.assetName]);

  // Open the location modal for a clicked location name (mirrors the Planning page behavior).
  const openPlanningLocation = useCallback(async (locationName) => {
    if (!locationName) return;
    let locationData = null;
    const found = planningLocations.find((loc) => loc.locationName === locationName || loc.subLocations?.some((s) => s.locationName === locationName));
    if (found) {
      locationData = found.locationName === locationName
        ? { ...found, organization: asset?.organization }
        : { ...found.subLocations.find((s) => s.locationName === locationName), organization: asset?.organization };
    }
    setPlanningLocationModal({ isOpen: true, title: t('shared.location_details', 'Location Details'), mainData: locationName, relatedData: [], loading: true, locationData });
    try {
      const { data } = await api.get('asset-issues/organization/filtered', { params: { organizationLocation: locationName } });
      setPlanningLocationModal((prev) => ({ ...prev, relatedData: data?.response || [], loading: false }));
    } catch (e) {
      setPlanningLocationModal((prev) => ({ ...prev, loading: false }));
    }
  }, [planningLocations, asset?.organization, t]);

  // Meters tab state
  const [assetMeters, setAssetMeters] = useState([]);
  const [loadingMeters, setLoadingMeters] = useState(false);
  const [detailMeterId, setDetailMeterId] = useState(null);
  const [unauthorized, setUnauthorized] = useState(false);
  const [outOfTeamScope, setOutOfTeamScope] = useState(false);

  // Status change modal
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Tab and UI states
  const [activeTab, setActiveTab] = useState('assetDetails');

  // Deep-link from Reporting → Asset Health → Maintenance Spend card uses ?tab=cost
  // to open the Cost tab directly. Only honored when the user has cost-report access.
  useEffect(() => {
    const requestedTab = searchParams.get('tab');
    if (requestedTab === 'cost' && canViewCostReport) {
      setActiveTab('cost');
    }
  }, [searchParams, canViewCostReport]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);

  // Downtime tab: default to last 30 days
  const downtimeDateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);
    return { startDate: start, endDate: end };
  }, []);

  // Work Orders states
  const [allWorkOrders, setAllWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Activities states
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);

  // Warranties states
  const [warranties, setWarranties] = useState([]);
  const [loadingWarranties, setLoadingWarranties] = useState(false);
  const [warrantySearchTerm, setWarrantySearchTerm] = useState('');

  // Sub Assets states
  const [subAssets, setSubAssets] = useState([]);
  const [loadingSubAssets, setLoadingSubAssets] = useState(false);

  // Spare Parts states
  const [sparePartsUsage, setSparePartsUsage] = useState([]);
  const [loadingSparePartsConsumables, setLoadingSparePartsConsumables] =
    useState(false);

  // Attach Spare Part modal states
  const [showAttachSparePartModal, setShowAttachSparePartModal] = useState(false);
  const [availableSpareParts, setAvailableSpareParts] = useState([]);
  const [selectedSparePartId, setSelectedSparePartId] = useState('');
  const [attachQuantity, setAttachQuantity] = useState(1);
  const [attachLoading, setAttachLoading] = useState(false);

  document.title = asset?.assetName
    ? `${asset.assetName} | ArcMedix`
    : `${t("shared.asset_detail", "Asset Detail")} | ArcMedix`;

  const fetchAssetByUuid = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      setUnauthorized(false);

      const response = await api.get(`/asset/get-by-uuid`, {
        params: { uuid },
      });

      if (response.data?.response) {
        setAsset(response.data.response);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      // Handle 403 - Unauthorized (asset belongs to another organization)
      if (
        err.response?.status === 403 ||
        err.response?.data?.message?.toLowerCase()?.includes('not allowed') ||
        err.response?.data?.message?.toLowerCase()?.includes('forbidden')
      ) {
        setUnauthorized(true);
      }
      // Handle 404 - Not found (UUID not attached to any asset)
      else if (
        err.response?.status === 404 ||
        err.response?.data?.message?.includes('not found') ||
        err.response?.data?.message?.includes('not-found-by-uuid')
      ) {
        setNotFound(true);
      } else {
        setError(err.response?.data?.message || t("business.asset_detail.failed_to_load", "Failed to load asset"));
      }
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchAssetByUuid();
  }, [fetchAssetByUuid]);

  // Back button handler with fallback
  const handleGoBack = useCallback(() => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/assets');
    }
  }, [navigate]);

  // Fetch activities
  const getActivity = useCallback(async () => {
    if (!asset?.id) return;
    try {
      setLoadingActivities(true);
      const { data } = await api.get(`asset/activity?parentId=${asset.id}`);
      setActivities(data.response || []);
    } catch (error) {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  }, [asset?.id]);

  // Fetch warranties
  const getWarranties = useCallback(async () => {
    if (!asset?.id) return;
    try {
      setLoadingWarranties(true);
      const { data } = await api.get(
        `asset-warranty-maintenance/get-all?assetId=${asset.id}${
          warrantySearchTerm ? `&search=${warrantySearchTerm}` : ''
        }`
      );
      setWarranties(data.response || []);
    } catch (error) {
      setWarranties([]);
    } finally {
      setLoadingWarranties(false);
    }
  }, [asset?.id, warrantySearchTerm]);

  // Fetch sub assets
  const getSubAssets = useCallback(() => {
    if (!asset?.id) return;
    setLoadingSubAssets(true);
    setSubAssets(asset?.children || []);
    setLoadingSubAssets(false);
  }, [asset]);

  // Fetch spare parts
  const fetchSpareParts = useCallback(async () => {
    if (!asset?.id) return;
    try {
      setLoadingSparePartsConsumables(true);
      const sparePartsRes = await api.get(`spare-parts/by-asset?assetId=${asset.id}`);
      setSparePartsUsage(sparePartsRes?.data?.response || []);
    } catch (error) {
      setSparePartsUsage([]);
    } finally {
      setLoadingSparePartsConsumables(false);
    }
  }, [asset?.id]);

  // Fetch meters for this asset
  const fetchAssetMeters = useCallback(async () => {
    if (!asset?.id) return;
    try {
      setLoadingMeters(true);
      const response = await api.get(`meter/by-asset?assetId=${asset.id}`);
      setAssetMeters(response?.data?.response || []);
    } catch (error) {
      setAssetMeters([]);
    } finally {
      setLoadingMeters(false);
    }
  }, [asset?.id]);

  // Open attach spare part modal
  const handleOpenAttachSparePart = useCallback(async () => {
    try {
      const orgId = asset?.organization?.id;
      if (!orgId) return;
      const res = await api.get(`spare-parts/all?organizationId=${orgId}`);
      setAvailableSpareParts(res?.data?.response || []);
      setSelectedSparePartId('');
      setAttachQuantity(1);
      setShowAttachSparePartModal(true);
    } catch (err) {
      console.error('Failed to fetch spare parts:', err);
    }
  }, [asset?.organization?.id]);

  // Submit attach spare part
  const handleAttachSparePart = useCallback(async () => {
    if (!selectedSparePartId || attachQuantity < 1 || !asset?.id) return;
    try {
      setAttachLoading(true);
      await api.post('spare-parts/attach-to-asset', {
        assetId: asset.id,
        inventory: [{ sparePartId: Number(selectedSparePartId), quantity: attachQuantity }]
      });
      setShowAttachSparePartModal(false);
      fetchSpareParts();
    } catch (err) {
      console.error('Failed to attach spare part:', err);
    } finally {
      setAttachLoading(false);
    }
  }, [selectedSparePartId, attachQuantity, asset?.id, fetchSpareParts]);

  // Fetch all work orders from the unified asset_issues table.
  // workType (REACTIVE / PREVENTIVE / CALIBRATION / TESTING / ROUTINE / RECALL)
  // is stored on the row itself, so a single endpoint covers every type.
  const fetchAllWorkOrders = useCallback(
    async (fromDateParam = null, toDateParam = null) => {
      if (!asset?.id) return;
      setLoadingWorkOrders(true);
      try {
        const assetId = asset.id;
        const params = { assetId };
        if (fromDateParam)
          params.fromDate = formatDateTimeForAPI(fromDateParam);
        if (toDateParam) params.toDate = formatDateTimeForAPI(toDateParam);

        const { data } = await api.get(`asset-issues/organization/filtered`, { params });
        const items = data?.response || [];

        const toTitle = (s) =>
          typeof s === 'string' && s.length > 0
            ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
            : s;

        const allOrders = items.map((item) => ({
          ...item,
          workType: toTitle(item.workType) || 'Reactive',
          ticketNumber: item.ticketNumber || item.requestId,
          requestDate: item.requestDate || item.dueDate || item.createdAt,
        }));
        allOrders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setAllWorkOrders(allOrders);
      } catch (error) {
        setAllWorkOrders([]);
      } finally {
        setLoadingWorkOrders(false);
      }
    },
    [asset?.id]
  );

  // Apply date filters
  const applyDateFilters = () => {
    if (!asset?.id) return;
    setIsFiltering(true);
    const adjustedFromDate = fromDate
      ? new Date(
          Date.UTC(
            fromDate.getFullYear(),
            fromDate.getMonth(),
            fromDate.getDate(),
            0,
            0,
            0
          )
        )
      : null;
    const adjustedToDate = toDate
      ? new Date(
          Date.UTC(
            toDate.getFullYear(),
            toDate.getMonth(),
            toDate.getDate(),
            23,
            59,
            59
          )
        )
      : null;
    fetchAllWorkOrders(adjustedFromDate, adjustedToDate).finally(() =>
      setIsFiltering(false)
    );
  };

  // Clear date filters
  const clearDateFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSearchTerm('');
    if (!asset?.id) return;
    setIsFiltering(true);
    fetchAllWorkOrders(null, null).finally(() => setIsFiltering(false));
  };

  // Tab-specific data loading
  useEffect(() => {
    if (asset?.id && activeTab === 'activities') getActivity();
  }, [asset?.id, activeTab, getActivity]);

  useEffect(() => {
    if (asset?.id && activeTab === 'warranties') getWarranties();
  }, [asset?.id, activeTab, getWarranties]);

  useEffect(() => {
    if (asset?.id && activeTab === 'subAssets') getSubAssets();
  }, [asset?.id, activeTab, getSubAssets]);

  useEffect(() => {
    if (asset?.id && activeTab === 'workOrders') fetchAllWorkOrders();
  }, [asset?.id, activeTab, fetchAllWorkOrders]);

  useEffect(() => {
    if (asset?.id && activeTab === 'spareParts')
      fetchSpareParts();
  }, [asset?.id, activeTab, fetchSpareParts]);

  // Fetch meters when meters tab is active
  useEffect(() => {
    if (asset?.id && activeTab === 'meters') fetchAssetMeters();
  }, [asset?.id, activeTab, fetchAssetMeters]);

  // Debounced warranty search
  useEffect(() => {
    if (activeTab === 'warranties' && asset?.id) {
      const timeoutId = setTimeout(() => getWarranties(), 300);
      return () => clearTimeout(timeoutId);
    }
  }, [warrantySearchTerm, activeTab, asset?.id, getWarranties]);

  // Filtered work orders
  const filteredWorkOrders = useMemo(() => {
    return allWorkOrders.filter(
      (issue) =>
        issue.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false ||
        issue.asset?.assetName
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        false ||
        issue.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false ||
        issue.workType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        false
    );
  }, [allWorkOrders, searchTerm]);

  // Filtered warranties
  const filteredWarranties = useMemo(() => {
    if (!warrantySearchTerm.trim()) return warranties;
    const searchLower = warrantySearchTerm.toLowerCase();
    return warranties.filter(
      (warranty) =>
        warranty?.contractNumber?.toLowerCase().includes(searchLower) ||
        warranty?.maintenanceVendorName?.toLowerCase().includes(searchLower) ||
        warranty?.notes?.toLowerCase().includes(searchLower)
    );
  }, [warranties, warrantySearchTerm]);

  // Helper functions
  const getStatusInfo = (status) => {
    const statusMap = {
      SUBMITTED: {
        label: t("shared.open", "Open"),
        icon: 'circle',
        colorClass: 'bg-primary bg-opacity-10 text-primary',
        borderColor: '#667eea',
      },
      IN_PROGRESS: {
        label: t("shared.in_progress", "In Progress"),
        icon: 'loader',
        colorClass: 'bg-warning bg-opacity-10 text-warning',
        borderColor: '#ffc107',
      },
      RESOLVED: {
        label: t("shared.resolved", "Resolved"),
        icon: 'check-circle',
        colorClass: 'bg-success bg-opacity-10 text-success',
        borderColor: '#28a745',
      },
      CLOSED: {
        label: t("shared.closed", "Closed"),
        icon: 'check-circle',
        colorClass: 'bg-secondary bg-opacity-10 text-secondary',
        borderColor: '#6c757d',
      },
      PENDING: {
        label: t("shared.pending", "Pending"),
        icon: 'clock',
        colorClass: 'bg-info bg-opacity-10 text-info',
        borderColor: '#17a2b8',
      },
      COMPLETED: {
        label: t("shared.completed", "Completed"),
        icon: 'check-circle',
        colorClass: 'bg-success bg-opacity-10 text-success',
        borderColor: '#28a745',
      },
    };
    return (
      statusMap[status] || {
        label: status?.replace(/_/g, ' ') || t("shared.unknown", "Unknown"),
        icon: 'help-circle',
        colorClass: 'bg-secondary bg-opacity-10 text-secondary',
        borderColor: '#6c757d',
      }
    );
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      ACTIVE: 'bg-success',
      COMPLETED: 'bg-success',
      IN_PROGRESS: 'bg-warning',
      PENDING: 'bg-warning',
      REJECTED: 'bg-danger',
      PERMANENT_TRANSFERRED: 'bg-info',
      TEMPORARY_TRANSFERRED: 'bg-primary',
      CANCELLED: 'bg-danger',
      INACTIVE: 'bg-secondary',
    };
    return statusClasses[status] || 'bg-secondary';
  };

  const getWarrantyStatusBadge = (warranty) => {
    if (warranty?.overallStatus === 'Active') return 'success';
    if (warranty?.overallStatus === 'Expired') return 'danger';
    return 'secondary';
  };

  const openImageModal = (index) => {
    setSelectedImageIndex(index);
    setShowImageModal(true);
  };

  const closeImageModal = () => setShowImageModal(false);

  const tableRowVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.05, type: 'spring', stiffness: 300 },
    }),
  };

  const handleNavigateToAssets = () => {
    navigate('/assets');
  };

  const handleCreateAsset = () => {
    // Navigate to assets page with createWithUuid param
    navigate(`/assets?createWithUuid=${uuid}`);
  };

  const handleAttachAsset = () => {
    // Navigate to assets page with attachUuid param
    navigate(`/assets?attachUuid=${uuid}`);
  };

  const getAssetStatusBadge = (status) => {
    const statusColors = {
      ONLINE: 'success',
      OFFLINE: 'danger',
      DO_NOT_TRACK: 'secondary',
      // Legacy fallbacks
      OFFLINE_PLANNED: 'danger',
      OFFLINE_UNPLANNED: 'danger',
      IN_USE: 'success',
      STANDBY: 'success',
      PLANNED_DOWNTIME: 'info',
      UNPLANNED_DOWNTIME: 'danger',
      AVAILABLE: 'secondary',
      UNAVAILABLE: 'secondary',
      DECOMMISSIONED: 'dark',
      DISPOSED: 'dark',
    };
    return statusColors[status] || 'secondary';
  };

  // Loading state
  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '60vh' }}
          >
            <div className="text-center">
              <Spinner
                color="primary"
                style={{ width: '3rem', height: '3rem' }}
              />
              <p className="mt-3 text-muted">{t("business.asset_detail.loading_asset", "Loading asset...")}</p>
              <code className="text-muted" style={{ fontSize: '0.75rem' }}>
                {t("shared.uuid", "UUID")}: {uuid}
              </code>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // 403 - Unauthorized state (asset belongs to another organization)
  if (unauthorized) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={6}>
              <Card className="shadow-lg border-0" style={{ marginTop: '5vh' }}>
                <CardBody className="text-center p-5">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#f8d7da',
                    }}
                  >
                    <FeatherIcon
                      icon="lock"
                      size={40}
                      style={{ color: '#721c24' }}
                    />
                  </div>

                  <h3 className="mb-3">{t("shared.access_denied", "Access Denied")}</h3>

                  <p className="text-muted mb-3">
                    {outOfTeamScope
                      ? t("teams.restrictVisibility.assetOutOfScope", "This asset is outside your team's scope.")
                      : t("business.asset_detail.access_denied_msg", "This asset belongs to another organization. You don't have permission to view its details.")}
                  </p>

                  <div
                    className="mb-4 p-2 rounded"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <small className="text-muted d-block mb-1">{t("shared.tag_uuid", "Tag UUID")}</small>
                    <code>{uuid}</code>
                  </div>

                  <div className="d-grid gap-2">
                    <Button
                      color="primary"
                      size="lg"
                      onClick={handleNavigateToAssets}
                      style={{ borderRadius: '8px' }}
                    >
                      {t("shared.go_to_assets_list", "Go to Assets List")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // 404 - Not found state (UUID not attached to any asset)
  if (notFound) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={6}>
              <Card className="shadow-lg border-0" style={{ marginTop: '5vh' }}>
                <CardBody className="text-center p-5">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#fff3cd',
                    }}
                  >
                    <FeatherIcon
                      icon="search"
                      size={40}
                      style={{ color: '#856404' }}
                    />
                  </div>

                  <h3 className="mb-3">{t("business.asset_detail.unregistered_tag", "Unregistered Tag")}</h3>

                  <p className="text-muted mb-3">
                    {t("business.asset_detail.tag_not_linked", "This NFC/RFID tag is not linked to any asset yet.")}
                  </p>

                  <div
                    className="mb-4 p-2 rounded"
                    style={{ backgroundColor: '#f8f9fa' }}
                  >
                    <small className="text-muted d-block mb-1">{t("shared.tag_uuid", "Tag UUID")}</small>
                    <code>{uuid}</code>
                  </div>

                  <div className="d-grid gap-2">
                    <Button
                      color="primary"
                      size="lg"
                      onClick={handleAttachAsset}
                      style={{ borderRadius: '8px' }}
                    >
                      <FeatherIcon icon="link" size={18} className="me-2" />
                      {t("business.asset_detail.attach_to_existing", "Attach to Existing Asset")}
                    </Button>
                    <Button
                      color="outline-primary"
                      size="lg"
                      onClick={handleCreateAsset}
                      style={{ borderRadius: '8px' }}
                    >
                      <FeatherIcon icon="plus" size={18} className="me-2" />
                      {t("business.asset_detail.create_new_asset", "Create New Asset")}
                    </Button>
                    <Button color="light" onClick={handleNavigateToAssets}>
                      {t("shared.go_to_assets_list", "Go to Assets List")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Row className="justify-content-center">
            <Col lg={6}>
              <Card className="shadow-lg border-0" style={{ marginTop: '5vh' }}>
                <CardBody className="text-center p-5">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                    style={{
                      width: '80px',
                      height: '80px',
                      backgroundColor: '#f8d7da',
                    }}
                  >
                    <FeatherIcon
                      icon="alert-circle"
                      size={40}
                      style={{ color: '#721c24' }}
                    />
                  </div>

                  <h3 className="mb-3">{t("shared.something_went_wrong", "Something went wrong")}</h3>

                  <Alert color="danger" className="mb-4">
                    {error}
                  </Alert>

                  <div className="d-grid gap-2">
                    <Button
                      color="primary"
                      onClick={fetchAssetByUuid}
                      style={{ borderRadius: '8px' }}
                    >
                      <FeatherIcon
                        icon="refresh-cw"
                        size={18}
                        className="me-2"
                      />
                      {t("business.asset_detail.try_again", "Try Again")}
                    </Button>
                    <Button color="light" onClick={handleNavigateToAssets}>
                      {t("shared.go_to_assets_list", "Go to Assets List")}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // Asset found - display details with tabbed interface matching AssetModal
  return (
    <div className="page-content">
      <Container fluid>
        <Card
          className="border-0 shadow-lg"
          style={{ borderRadius: '12px', overflow: 'hidden' }}
        >
          {/* Enhanced Header matching AssetModal */}
          <div
            className="border-0 text-white position-sticky d-flex justify-content-between align-items-center"
            style={{
              top: 0,
              zIndex: 10,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '1.5rem',
            }}
          >
            <div className="d-flex align-items-center">
              <Button
                color="light"
                size="sm"
                onClick={handleGoBack}
                className="me-3 d-flex align-items-center justify-content-center"
                style={{ width: '36px', height: '36px', borderRadius: '50%' }}
              >
                <FeatherIcon icon="arrow-left" size={18} />
              </Button>
              <div className="avatar-sm bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3">
                <i className="bx bx-cube text-white fs-4"></i>
              </div>
              <div>
                <h4 className="mb-1 text-white">{asset?.assetName}</h4>
                <p className="mb-0 text-white-50 small">
                  <i className="bx bx-barcode me-1"></i>
                  {t("shared.asset_id", "Asset ID")}: {asset?.assetNumber || t("shared.not_assigned", "Not assigned")}
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              {asset?.assetStatus && (
                <Badge
                  color={getAssetStatusBadge(asset.assetStatus)}
                  className="px-3 py-2"
                  style={{ cursor: isRequestor ? 'default' : 'pointer' }}
                  onClick={isRequestor ? undefined : () => setShowStatusModal(true)}
                  title={isRequestor ? undefined : t("shared.click_to_change_status", "Click to change status")}
                >
                  {t(`shared.${asset.assetStatus.toLowerCase()}`, asset.assetStatus)}
                  {asset.assetStatus === 'OFFLINE' && asset.downtimeReasonName ? ` (${t(`shared.${asset.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, asset.downtimeReasonName)})` : ''}
                </Badge>
              )}
              {!isRequestor && (
                <Button
                  size="sm"
                  color="light"
                  className="text-white border-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                  onClick={() => setShowStatusModal(true)}
                >
                  {t("shared.buttons_change_status", "Change Status")}
                </Button>
              )}
              {/* Hidden while the asset is awaiting hand-over to another organization; the badge
                  beside it says which organization is expected to accept it. */}
              {!isRequestor && canTransferAsset && asset?.organization?.id
                && !asset?.pendingCrossOrgTransfer && (
                <Button
                  size="sm"
                  color="light"
                  className="text-white border-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                  onClick={() => setShowTransferModal(true)}
                >
                  <i className="bx bx-transfer-alt me-1"></i>
                  {t("business.planning.transfer", "Transfer")}
                </Button>
              )}
              {asset?.pendingCrossOrgTransfer && (
                <Badge color="warning" className="align-self-center">
                  <i className="bx bx-transfer-alt me-1"></i>
                  {t('business.planning.pending_outgoing_transfer', 'Transfer pending → {{organization}}', {
                    organization: asset.pendingCrossOrgTransfer.destinationOrganizationName || '',
                  })}
                </Badge>
              )}
              {hasPermission(PROCUREMENT_REQUEST_CREATE) && asset?.id && (
                <Button
                  size="sm"
                  color="light"
                  className="text-white border-0"
                  style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                  onClick={() => navigate('/planning', {
                    state: {
                      requestPrefill: {
                        assetId: asset.id,
                        assetName: asset.assetName,
                        locationId: asset.locationId || null,
                      },
                    },
                  })}
                >
                  <i className="bx bx-cart-alt me-1"></i>
                  {t("planning_purchasing.new_request", "New Request")}
                </Button>
              )}
            </div>
          </div>

          {/* Navigation Tabs */}
          <Nav
            tabs
            className="border-bottom-0 px-4 pt-3 bg-light"
            style={{ borderRadius: '0' }}
          >
            <NavItem>
              <NavLink
                className={`nav-link-modern ${
                  activeTab === 'assetDetails' ? 'active' : ''
                }`}
                onClick={() => setActiveTab('assetDetails')}
                style={{
                  border: 'none',
                  borderRadius: '8px 8px 0 0',
                  padding: '12px 20px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                <i className="bx bx-info-circle me-2"></i>{t("shared.asset_details", "Asset Details")}
              </NavLink>
            </NavItem>
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'workOrders' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('workOrders')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bx bx-list-ul me-2"></i>{t("shared.work_orders", "Work Orders")}
                  {allWorkOrders.length > 0 && (
                    <Badge
                      color="primary"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {allWorkOrders.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'planning' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('planning')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bx bx-list-ul me-2"></i>{t("shared.planning", "Planning")}
                  {asset?.equipmentProcesses?.length > 0 && (
                    <Badge
                      color="primary"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {asset.equipmentProcesses.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'warranties' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('warranties')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bx bx-shield-check me-2"></i>{t("shared.service_contracts", "Service Contracts")}
                  {warranties.length > 0 && (
                    <Badge
                      color="success"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {warranties.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'subAssets' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('subAssets')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <i className="bx bx-sitemap me-2"></i>{t("shared.sub_assets", "Sub Assets")}
                  {subAssets.length > 0 && (
                    <Badge
                      color="info"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {subAssets.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'activities' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('activities')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t("shared.activity_log", "Activity Log")}
                  {activities.length > 0 && (
                    <Badge
                      color="secondary"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {activities.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'spareParts' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('spareParts')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <FeatherIcon icon="tool" size={16} className="me-2" />
                  {t("shared.spare_parts", "Spare Parts")}
                  {sparePartsUsage.length > 0 && (
                    <Badge
                      color="primary"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {sparePartsUsage.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'meters' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('meters')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <FeatherIcon icon="activity" size={16} className="me-2" />
                  {t("shared.meters", "Meters")}
                  {assetMeters.length > 0 && (
                    <Badge
                      color="primary"
                      pill
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {assetMeters.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            )}
            {!isRequestor && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'downtime' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('downtime')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <FeatherIcon icon="trending-down" size={16} className="me-2" />
                  {t("business.assets.downtime_tab", "Downtime")}
                </NavLink>
              </NavItem>
            )}
            {canViewCostReport && (
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${
                    activeTab === 'cost' ? 'active' : ''
                  }`}
                  onClick={() => setActiveTab('cost')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  <FeatherIcon icon="dollar-sign" size={16} className="me-2" />
                  {t("cost_tracking.cost_tab", "Cost")}
                </NavLink>
              </NavItem>
            )}
          </Nav>

          {/* Tab Content */}
          <TabContent activeTab={activeTab} className="h-100">
            {/* Asset Details Tab */}
            <TabPane tabId="assetDetails">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div
                          className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className="bx bx-cube text-primary fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t("shared.asset_information", "Asset Information")}</h5>
                        <p className="text-muted small mb-0">
                          {t("shared.complete_asset_specifications_and_detail", "Complete asset specifications and details")}
                        </p>
                      </div>
                    </div>

                    <Row className="g-4">
                      {/* Basic Information */}
                      <Col md={6}>
                        <div className="info-card bg-light rounded p-3 h-100">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-info-circle me-2"></i>{t("shared.basic_information", "Basic Information")}
                          </h6>
                          <div className="info-item mb-3">
                            <label className="text-muted small">
                              {t("shared.asset_name", "Asset Name")}
                            </label>
                            <div className="fw-medium">
                              {asset?.assetName || t("shared.n_a", "N/A")}
                            </div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t("shared.asset_id", "Asset ID")}</label>
                            <div className="fw-medium">
                              {asset?.assetNumber || t("shared.n_a", "N/A")}
                            </div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">
                              {t("shared.serial_number", "Serial Number")}
                            </label>
                            <div className="fw-medium">
                              {asset?.serialNumber || t("shared.n_a", "N/A")}
                            </div>
                          </div>
                          {!isRequestor && (
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.model_number", "Model Number")}
                              </label>
                              <div className="fw-medium">
                                {asset?.modelNumber || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                          )}
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t("shared.status", "Status")}</label>
                            <div>
                              <Badge color={getAssetStatusBadge(asset?.assetStatus)} className="px-2 py-1">
                                {asset?.assetStatus ? t(`shared.${asset?.assetStatus.toLowerCase()}`, asset?.assetStatus) : t("shared.n_a", "N/A")}
                                {asset?.assetStatus === 'OFFLINE' && asset?.downtimeReasonName ? ` (${t(`shared.${asset.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, asset.downtimeReasonName)})` : ''}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Other Details - Show only Location for requesters */}
                      {isRequestor ? (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-info mb-3 fw-semibold">
                              <i className="bx bx-map me-2"></i>{t("shared.location", "Location")}
                            </h6>
                            <div className="info-item mb-0">
                              <label className="text-muted small">{t("shared.location", "Location")}</label>
                              <div className="fw-medium">
                                {asset?.parentLocationName
                                  ? `${asset.parentLocationName} → ${asset.currentLocation}`
                                  : (asset?.currentLocation || t("shared.n_a", "N/A"))}
                              </div>
                            </div>
                          </div>
                        </Col>
                      ) : (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-success mb-3 fw-semibold">
                              <i className="bx bx-cog me-2"></i>{t("shared.other_details", "Other Details")}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.manufacturer", "Manufacturer")}
                              </label>
                              <div className="fw-medium">
                                {asset?.manufacturer || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.asset_types", "Asset Types")}
                              </label>
                              <div className="fw-medium">
                                {asset?.assetTypes?.map(at => at.name).join(', ') || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.location", "Location")}
                              </label>
                              <div className="fw-medium">
                                {asset?.parentLocationName
                                  ? `${asset.parentLocationName} → ${asset.currentLocation}`
                                  : (asset?.currentLocation || t("shared.n_a", "N/A"))}
                              </div>
                            </div>
                            {asset?.assetStatus === 'OFFLINE' && asset?.downtimeReasonName && (
                              <div className="info-item mb-3">
                                <label className="text-muted small">
                                  {t("shared.downtime_reason", "Downtime Reason")}
                                </label>
                                <div className="fw-medium">
                                  {t(`shared.${asset.downtimeReasonName?.toLowerCase().replace(/\s+/g, '_')}`, asset.downtimeReasonName)}
                                </div>
                              </div>
                            )}
                            {asset?.assetStatus === 'DECOMMISSIONED' && (
                              <>
                                {asset?.decommissionedReason && (
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">
                                      {t("business.assets.decommission_reason", "Decommission reason")}
                                    </label>
                                    <div className="fw-medium">{asset.decommissionedReason}</div>
                                  </div>
                                )}
                                {asset?.decommissionedOn && (
                                  <div className="info-item mb-3">
                                    <label className="text-muted small">
                                      {t("business.assets.decommissioned_on", "Decommissioned on")}
                                    </label>
                                    <div className="fw-medium">{asset.decommissionedOn}</div>
                                  </div>
                                )}
                              </>
                            )}
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.vendor_name", "Vendor Name")}
                              </label>
                              <div className="fw-medium">
                                {asset?.vendorName || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">
                                {t("shared.criticality", "Criticality")}
                              </label>
                              <div className="fw-medium">
                                {asset?.criticality
                                  ? t(`shared.${asset.criticality.toLowerCase()}`,
                                      asset.criticality.charAt(0).toUpperCase() + asset.criticality.slice(1).toLowerCase())
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                          </div>
                        </Col>
                      )}

                      {/* Acquisition & Financial Information - Hidden for requesters */}
                      {!isRequestor && (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-success mb-3 fw-semibold">
                              <i className="bx bx-dollar-circle me-2"></i>
                              {asset?.acquisitionType && asset.acquisitionType !== 'PURCHASED'
                                ? t("business.assets.acquisition_information", "Acquisition Information")
                                : t("shared.financial_information", "Financial Information")}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">{t("business.assets.acquisition_type", "Acquisition Type")}</label>
                              <div className="fw-medium">
                                <Badge color={
                                  { 'PURCHASED': 'success', 'RENTAL': 'info', 'DEMO': 'warning', 'PLACEMENT': 'primary', 'DONATION': 'secondary' }[asset?.acquisitionType] || 'success'
                                } className="px-2 py-1">
                                  {{ 'PURCHASED': t('business.assets.purchased', 'Purchased'),
                                     'RENTAL': t('business.assets.rental', 'Rental'),
                                     'DEMO': t('business.assets.demo', 'Demo'),
                                     'PLACEMENT': t('business.assets.placement', 'Placement'),
                                     'DONATION': t('business.assets.donation', 'Donation/Grant'),
                                  }[asset?.acquisitionType] || t('business.assets.purchased', 'Purchased')}
                                </Badge>
                              </div>
                            </div>
                            {['RENTAL', 'DEMO', 'PLACEMENT'].includes(asset?.acquisitionType) && (<>
                              <div className="info-item mb-3">
                                <label className="text-muted small">
                                  {{ 'RENTAL': t('business.assets.rental_start_date', 'Rental Start Date'),
                                     'DEMO': t('business.assets.demo_start_date', 'Demo Start Date'),
                                     'PLACEMENT': t('business.assets.placement_start_date', 'Placement Start Date'),
                                  }[asset.acquisitionType]}
                                </label>
                                <div className="fw-medium">{asset?.acquisitionStartDate || t("shared.n_a", "N/A")}</div>
                              </div>
                              <div className="info-item mb-0">
                                <label className="text-muted small">
                                  {{ 'RENTAL': t('business.assets.rental_end_date', 'Rental End Date'),
                                     'DEMO': t('business.assets.demo_end_date', 'Demo End Date'),
                                     'PLACEMENT': t('business.assets.placement_end_date', 'Placement End Date'),
                                  }[asset.acquisitionType]}
                                </label>
                                <div className="fw-medium">{asset?.acquisitionEndDate || t("shared.n_a", "N/A")}</div>
                              </div>
                            </>)}
                            {(!asset?.acquisitionType || asset?.acquisitionType === 'PURCHASED') && (<>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.original_cost", "Original Cost")}
                              </label>
                              <div className="fw-medium">
                                {asset?.assetCost
                                  ? asset.assetCost.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: asset?.organization?.currency || asset?.currency || 'USD',
                                    })
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">{t("shared.currency", "Currency")}</label>
                              <div className="fw-medium">
                                {asset?.organization?.currency || asset?.currency || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.year_of_purchase", "Year of Purchase")}
                              </label>
                              <div className="fw-medium">
                                {asset?.yearOfPurchase || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">
                                {t("shared.commissioning_date", "Commissioning Date")}
                              </label>
                              <div className="fw-medium">
                                {asset?.commissioningDate || t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            </>)}
                          </div>
                        </Col>
                      )}

                      {/* Depreciation - Only for PURCHASED assets, hidden for requesters */}
                      {!isRequestor && (!asset?.acquisitionType || asset?.acquisitionType === 'PURCHASED') && (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-success mb-3 fw-semibold">
                              <i className="bx bx-trending-down me-2"></i>{t("shared.depreciation", "Depreciation")}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.estimated_lifespan", "Estimated Lifespan")}
                              </label>
                              <div className="fw-medium">
                                {asset?.estimatedLifespan
                                  ? `${asset.estimatedLifespan} ${t('shared.years', 'years')}`
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            {asset?.depreciationPeriodYears && (
                              <div className="info-item mb-3">
                                <label className="text-muted small">
                                  {t("shared.accelerated_depreciation", "Accelerated Depreciation")}
                                </label>
                                <div className="fw-medium">
                                  {asset.depreciationPeriodYears} {t('shared.years', 'years')}
                                </div>
                              </div>
                            )}
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.depreciation_rate", "Depreciation Rate")}
                              </label>
                              <div className="fw-medium">
                                {asset?.depreciationRate
                                  ? `${asset.depreciationRate}%`
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.annual_depreciation", "Annual Depreciation")}
                              </label>
                              <div className="fw-medium">
                                {asset?.annualDepreciation
                                  ? asset.annualDepreciation.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: asset?.organization?.currency || asset?.currency || 'USD',
                                    })
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("shared.accumulated_depreciation", "Accumulated Depreciation")}
                              </label>
                              <div className="fw-medium">
                                {asset?.accumulatedDepreciation
                                  ? asset.accumulatedDepreciation.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: asset?.organization?.currency || asset?.currency || 'USD',
                                    })
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">
                                {t("shared.net_book_value", "Net Book Value")}
                              </label>
                              <div className="fw-medium">
                                {asset?.netBookValue
                                  ? asset.netBookValue.toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: asset?.organization?.currency || asset?.currency || 'USD',
                                    })
                                  : t("shared.n_a", "N/A")}
                              </div>
                            </div>
                          </div>
                        </Col>
                      )}

                      {/* Total Cost of Ownership panel (admin-only). Lifetime composition:
                          acquisition + depreciation (informational) + cumulative maintenance.
                          Sits above the period-filtered rollup card so the "big picture" lands
                          first. */}
                      {!isRequestor && asset?.id && (
                        <Col md={12}>
                          <AssetTCOPanel assetId={asset.id} />
                        </Col>
                      )}

                      {/* Cumulative maintenance cost rollup (admin-only). Sits next to the
                          depreciation / downtime info-cards so the financial story for the asset
                          stays in one place. */}
                      {!isRequestor && asset?.id && (
                        <Col md={12}>
                          <AssetCostRollupCard assetId={asset.id} />
                        </Col>
                      )}

                      {/* Downtime Cost Estimates - Hidden for requesters */}
                      {!isRequestor && (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="text-success mb-3 fw-semibold">
                              <i className="bx bx-time-five me-2"></i>
                              {t("business.assets.downtime_cost_estimates", "Downtime Cost Estimates")}
                            </h6>
                            <div className="info-item mb-3">
                              <label className="text-muted small">
                                {t("business.assets.average_price_per_case", "Average price by case")}
                              </label>
                              <div className="fw-medium">
                                {asset?.averagePricePerCase
                                  ? Number(asset.averagePricePerCase).toLocaleString('en-US', {
                                      style: 'currency',
                                      currency: asset?.organization?.currency || asset?.currency || 'USD',
                                    })
                                  : '\u2014'}
                              </div>
                            </div>
                            <div className="info-item mb-0">
                              <label className="text-muted small">
                                {t("business.assets.average_duration_per_case", "Average duration by case (hours)")}
                              </label>
                              <div className="fw-medium">
                                {asset?.averageDurationPerCaseHours
                                  ? `${asset.averageDurationPerCaseHours} ${t('shared.hours_abbr', 'h')}`
                                  : '\u2014'}
                              </div>
                            </div>
                          </div>
                        </Col>
                      )}

                      {/* Custom Fields - Hidden for requesters */}
                      {!isRequestor && Array.isArray(asset?.customFields) && asset.customFields.length > 0 && (
                        <Col md={6}>
                          <div className="info-card bg-light rounded p-3 h-100">
                            <h6 className="mb-3 fw-semibold" style={{ color: '#7c3aed' }}>
                              <i className="bx bx-data me-2"></i>{t("shared.custom_fields", "Custom Fields")}
                            </h6>
                            {asset.customFields.map((field, index) => {
                              const renderValue = () => {
                                const v = field.value;
                                if (v == null || v === '') return t("shared.n_a", "N/A");
                                switch (field.fieldType) {
                                  case 'CHECKBOX':
                                    return v === 'true' ? t("shared.yes", "Yes") : t("shared.no", "No");
                                  case 'DATE': {
                                    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
                                    return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
                                  }
                                  case 'DATETIME': {
                                    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(v);
                                    return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : v;
                                  }
                                  case 'MULTISELECT': {
                                    try {
                                      const arr = JSON.parse(v);
                                      return Array.isArray(arr) && arr.length ? arr.join(', ') : t("shared.n_a", "N/A");
                                    } catch { return String(v); }
                                  }
                                  case 'LINK': {
                                    const href = v.startsWith('tel:') || /^https?:\/\//i.test(v) ? v : `https://${v}`;
                                    return <a href={href} target="_blank" rel="noopener noreferrer">{v}</a>;
                                  }
                                  default:
                                    return String(v);
                                }
                              };
                              return (
                                <div
                                  key={field.definitionId ?? index}
                                  className={`info-item ${index < asset.customFields.length - 1 ? 'mb-3' : 'mb-0'}`}
                                >
                                  <label className="text-muted small">{field.label}</label>
                                  <div className="fw-medium">{renderValue()}</div>
                                </div>
                              );
                            })}
                          </div>
                        </Col>
                      )}

                      {/* Description */}
                      <Col md={12}>
                        <div className="info-card bg-light rounded p-3">
                          <h6 className="text-warning mb-3 fw-semibold">
                            <i className="bx bx-file-blank me-2"></i>{t("shared.description", "Description")}
                          </h6>
                          <div className="info-item mb-0">
                            <div
                              className="text-wrap"
                              style={{ lineHeight: '1.5' }}
                            >
                              {asset?.description || t("shared.no_description_available", "No description available")}
                            </div>
                          </div>
                        </div>
                      </Col>

                      {/* Asset Images */}
                      {asset?.assetImageUrls?.length > 0 && (
                        <Col md={12}>
                          <div className="info-card bg-light rounded p-3">
                            <h6 className="text-primary mb-3 fw-semibold">
                              <i className="bx bx-image me-2"></i>{t("shared.asset_images", "Asset Images")}
                              <Badge
                                color="primary"
                                className="ms-2"
                                style={{ fontSize: '10px' }}
                              >
                                {asset.assetImageUrls.length}
                              </Badge>
                            </h6>
                            <div className="row g-2">
                              {asset.assetImageUrls.map((imageUrl, index) => (
                                <div
                                  key={index}
                                  className="col-md-3 col-sm-4 col-6"
                                >
                                  <motion.div
                                    className="image-thumbnail position-relative"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    onClick={() => openImageModal(index)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <img
                                      src={imageUrl}
                                      alt={`Asset image ${index + 1}`}
                                      className="img-fluid rounded"
                                      style={{
                                        width: '100%',
                                        height: '120px',
                                        objectFit: 'cover',
                                        border: '2px solid #dee2e6',
                                      }}
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                      }}
                                    />
                                  </motion.div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Col>
                      )}

                      {/* QR Code and NFC Tag - Hidden for requesters */}
                      {!isRequestor && (asset?.qrImageUrl || asset?.uuid) && (
                        <Col md={12}>
                          <div className="info-card">
                            <Row className="g-3">
                              {/* QR Code */}
                              {asset?.qrImageUrl && (
                                <Col md={asset?.uuid ? 6 : 12}>
                                  <div className="bg-light rounded p-3 h-100">
                                    <h6 className="text-secondary mb-3 fw-semibold">
                                      <i className="bx bx-qr-scan me-2"></i>{t("shared.qr_code", "QR Code")}
                                    </h6>
                                    <div className="text-center">
                                      <img
                                        src={asset.qrImageUrl}
                                        alt="Asset QR Code"
                                        className="img-fluid"
                                        style={{
                                          maxWidth: '150px',
                                          border: '2px solid #dee2e6',
                                          borderRadius: '8px',
                                        }}
                                      />
                                      <div className="mt-2">
                                        <small className="text-muted">
                                          {t("shared.scan_to_view_asset_details", "Scan to view asset details")}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              )}

                              {/* NFC Tag */}
                              {asset?.uuid && (
                                <Col md={asset?.qrImageUrl ? 6 : 12}>
                                  <div className="bg-light rounded p-3 h-100">
                                    <h6 className="text-secondary mb-3 fw-semibold">
                                      <i className="bx bx-chip me-2"></i>{t("shared.nfc_tag", "NFC Tag")}
                                    </h6>
                                    <div className="text-center">
                                      <div
                                        className="d-inline-flex align-items-center justify-content-center mb-2"
                                        style={{
                                          width: '80px',
                                          height: '80px',
                                          backgroundColor: '#e8f4fd',
                                          borderRadius: '12px',
                                          border: '2px solid #dee2e6',
                                        }}
                                      >
                                        <i className="bx bx-wifi" style={{ fontSize: '40px', color: '#0d6efd', transform: 'rotate(45deg)' }}></i>
                                      </div>
                                      <div className="mt-2">
                                        <code style={{ fontSize: '0.75rem', color: '#d63384', wordBreak: 'break-all' }}>
                                          {asset.uuid}
                                        </code>
                                      </div>
                                      <div className="mt-1">
                                        <small className="text-muted">
                                          {t("shared.tap_nfc_tag_to_view_asset", "Tap NFC tag to view asset")}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </div>
                        </Col>
                      )}
                    </Row>
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Work Orders Tab */}
            <TabPane tabId="workOrders">
              <div className="p-4">
                <Card className="border-0 shadow-sm mb-4">
                  <CardBody className="p-3">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bx bx-filter-alt text-primary me-2 fs-5"></i>
                      <h6 className="mb-0 text-muted">{t("shared.filters_and_search", "Filters & Search")}</h6>
                    </div>
                    <Row className="g-3">
                      <Col md={4}>
                        <InputGroup>
                          <span className="input-group-text bg-transparent border-end-0">
                            <i className="bx bx-search text-muted"></i>
                          </span>
                          <Input
                            type="text"
                            placeholder={t("shared.search_tickets_assets_status", "Search tickets, assets, status...")}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="border-start-0 ps-0"
                          />
                        </InputGroup>
                      </Col>
                      <Col md={3}>
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          selectsStart
                          startDate={fromDate}
                          endDate={toDate}
                          className="form-control"
                          placeholderText={t("shared.from_date", "From date")}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </Col>
                      <Col md={3}>
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          selectsEnd
                          startDate={fromDate}
                          endDate={toDate}
                          minDate={fromDate}
                          className="form-control"
                          placeholderText={t("shared.to_date", "To date")}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </Col>
                      <Col md={2} className="d-flex gap-2">
                        <Button
                          color="primary"
                          size="sm"
                          onClick={applyDateFilters}
                          disabled={isFiltering || (!fromDate && !toDate)}
                          className="flex-fill"
                        >
                          {isFiltering ? (
                            <Spinner size="sm" className="me-1" />
                          ) : (
                            <i className="bx bx-check me-1"></i>
                          )}
                          {t("shared.apply", "Apply")}
                        </Button>
                        <Button
                          color="outline-secondary"
                          size="sm"
                          onClick={clearDateFilters}
                          disabled={
                            isFiltering || (!fromDate && !toDate && !searchTerm)
                          }
                        >
                          <i className="bx bx-x"></i>
                        </Button>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>

                <div
                  style={{ maxHeight: 'calc(90vh - 400px)', overflow: 'auto' }}
                >
                  {loadingWorkOrders || isFiltering ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                      <p className="text-muted mt-3">
                        {isFiltering
                          ? t("shared.applying_filters", "Applying filters...")
                          : t("shared.loading_work_orders", "Loading work orders...")}
                      </p>
                    </div>
                  ) : filteredWorkOrders.length > 0 ? (
                    <div className="table-responsive">
                      <Table hover className="mb-0 modern-table">
                        <thead
                          className="table-light position-sticky top-0"
                          style={{ zIndex: 0 }}
                        >
                          <tr>
                            <th className="border-0 py-3">
                              <i className="bx bx-hash me-1 text-muted"></i>
                              {t("shared.ticket", "Ticket")}
                            </th>
                            <th className="border-0 py-3">
                              <i className="bx bx-hash me-1 text-muted"></i>{t("shared.type", "Type")}
                            </th>
                            <th className="border-0 py-3">
                              <i className="bx bx-calendar me-1 text-muted"></i>
                              {t("shared.created_at", "Created At")}
                            </th>
                            <th className="border-0 py-3">
                              <i className="bx bx-calendar me-1 text-muted"></i>
                              {t("shared.due_at", "Due At")}
                            </th>
                            <th className="border-0 py-3">
                              <i className="bx bx-package me-1 text-muted"></i>
                              {t("shared.asset", "Asset")}
                            </th>
                            <th className="border-0 py-3">
                              <i className="bx bx-flag me-1 text-muted"></i>
                              {t("shared.status", "Status")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <AnimatePresence>
                            {filteredWorkOrders.map((issue, index) => {
                              const statusInfo = getStatusInfo(issue.status);
                              return (
                                <motion.tr
                                  key={issue.id || index}
                                  custom={index}
                                  variants={tableRowVariants}
                                  initial="hidden"
                                  animate="visible"
                                  exit="hidden"
                                  whileHover={{ backgroundColor: '#f8f9fa' }}
                                >
                                  <td className="py-3">
                                    <div
                                      className="ticket-number-badge"
                                      style={{
                                        background:
                                          'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        padding: '4px 12px',
                                        borderRadius: '20px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        display: 'inline-block',
                                      }}
                                    >
                                      {issue.ticketNumber}
                                    </div>
                                  </td>
                                  <td className="py-3">
                                    <Badge
                                      style={{
                                        backgroundColor:
                                          issue?.workType === 'Reactive'
                                            ? '#4285f4'
                                            : issue?.workType === 'Calibration'
                                            ? '#ea4335'
                                            : issue?.workType === 'Testing'
                                            ? '#fbbc04'
                                            : issue?.workType === 'Routine'
                                            ? '#34a853'
                                            : issue?.workType === 'Recall'
                                            ? '#9334e6'
                                            : issue?.workType === 'Preventive'
                                            ? '#ff6d00'
                                            : '#5f6368',
                                        color: 'white',
                                        fontSize: '11px',
                                        padding: '4px 10px',
                                        fontWeight: '600',
                                        borderRadius: '12px',
                                      }}
                                    >
                                      {issue?.workType || t("shared.n_a", "N/A")}
                                    </Badge>
                                  </td>
                                  <td className="py-3 text-muted">
                                    {formatCreatedDate(issue.createdAt)}
                                  </td>
                                  <td className="py-3 text-muted">
                                    {formatCreatedDate(issue.requestDate)}
                                  </td>
                                  <td className="py-3">
                                    <div className="d-flex align-items-center">
                                      <div className="avatar-xs bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                        <i className="bx bx-package text-primary"></i>
                                      </div>
                                      <span className="fw-medium">
                                        {issue.asset?.assetName ||
                                          asset?.assetName ||
                                          t("shared.n_a", "N/A")}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="py-3">
                                    <Badge
                                      className={`d-inline-flex align-items-center ${statusInfo.colorClass}`}
                                      style={{
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        fontSize: '11px',
                                        fontWeight: '500',
                                        border: `1px solid ${statusInfo.borderColor}`,
                                      }}
                                    >
                                      <FeatherIcon
                                        icon={statusInfo.icon}
                                        size={12}
                                        className="me-1"
                                      />
                                      {statusInfo.label}
                                    </Badge>
                                  </td>
                                </motion.tr>
                              );
                            })}
                          </AnimatePresence>
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                        <i className="bx bx-search text-muted fs-1"></i>
                      </div>
                      <h5 className="text-muted">{t("shared.no_work_orders_found", "No work orders found")}</h5>
                      <p className="text-muted mb-0">
                        {searchTerm || fromDate || toDate
                          ? t("shared.try_adjusting_filters", "Try adjusting your filters or search terms")
                          : t("shared.this_asset_has_no_reported_work_orders", "This asset has no reported work orders")}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </TabPane>

            {/* Planning Tab */}
            <TabPane tabId="planning">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div
                          className="bg-success bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className="bx bx-shield-check text-success fs-5"></i>
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t("shared.planning", "Planning")}</h5>
                        <p className="text-muted small mb-0">
                          {t("shared.manage_planning_requests", "Manage Planning Requests")}
                        </p>
                      </div>
                    </div>
                    {/* Acquisition Info Banner */}
                    {['RENTAL', 'DEMO', 'PLACEMENT'].includes(asset?.acquisitionType) && (() => {
                      const endDate = asset?.acquisitionEndDate;
                      let daysRemaining = null;
                      let bannerColor = 'info';
                      if (endDate) {
                        const parts = endDate.split('-');
                        const end = parts[0].length === 2
                          ? new Date(parts[2], parts[1] - 1, parts[0])
                          : new Date(parts[0], parts[1] - 1, parts[2]);
                        daysRemaining = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
                        if (daysRemaining < 0) bannerColor = 'danger';
                        else if (daysRemaining <= 30) bannerColor = 'warning';
                      }
                      const typeLabel = {
                        'RENTAL': t('business.assets.rental', 'Rental'),
                        'DEMO': t('business.assets.demo', 'Demo'),
                        'PLACEMENT': t('business.assets.placement', 'Placement'),
                      }[asset.acquisitionType];
                      return (
                        <Alert color={bannerColor} className="mb-4 d-flex align-items-center justify-content-between">
                          <div>
                            <i className="bx bx-info-circle me-2"></i>
                            <strong>{typeLabel}</strong>
                            {asset?.acquisitionStartDate && <span className="ms-2">{t('shared.from', 'From')}: {asset.acquisitionStartDate}</span>}
                            {asset?.acquisitionEndDate && <span className="ms-2">{t('shared.to', 'To')}: {asset.acquisitionEndDate}</span>}
                          </div>
                          {daysRemaining !== null && (
                            <Badge color={bannerColor === 'info' ? 'info' : bannerColor} className="ms-2">
                              {daysRemaining < 0
                                ? t('business.assets.overdue_days', '{{count}} days overdue', { count: Math.abs(daysRemaining) })
                                : t('business.assets.days_remaining', '{{count}} days remaining', { count: daysRemaining })}
                            </Badge>
                          )}
                        </Alert>
                      );
                    })()}

                    <div
                      style={{
                        maxHeight: 'calc(90vh - 400px)',
                        overflow: 'auto',
                      }}
                    >
                      {asset?.equipmentProcesses?.length > 0 ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 modern-table">
                            <thead className="table-light">
                              <tr>
                                <th className="border-0 py-3">{t("shared.request_id", "Request ID")}</th>
                                <th className="border-0 py-3">{t("shared.start_date", "Start Date")}</th>
                                <th className="border-0 py-3">{t("shared.end_date", "End Date")}</th>
                                <th className="border-0 py-3">{t("shared.location", "Location")}</th>
                                <th className="border-0 py-3">{t("shared.status", "Status")}</th>
                                <th className="border-0 py-3">{t("shared.reason", "Reason")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <AnimatePresence>
                                {asset.equipmentProcesses.map(
                                  (issue, index) => (
                                    <motion.tr
                                      key={issue.id || index}
                                      custom={index}
                                      variants={tableRowVariants}
                                      initial="hidden"
                                      animate="visible"
                                      exit="hidden"
                                      whileHover={{
                                        backgroundColor: '#f8f9fa',
                                      }}
                                      className="cursor-pointer"
                                      onClick={() => openTransferDetails(issue)}
                                    >
                                      <td className="py-3">
                                        {transferByProcess[issue.id]?.requestId ? (
                                          <span className="text-primary fw-medium" role="button" style={{ cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); openTransferDetails(issue); }}>
                                            {transferByProcess[issue.id].requestId}
                                          </span>
                                        ) : (
                                          <span className="text-muted">{index + 1}</span>
                                        )}
                                      </td>
                                      <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.startDate)}
                                      </td>
                                      <td className="py-3 text-muted">
                                        {formatCreatedDate(issue.endDate)}
                                      </td>
                                      <td className="py-3">
                                        {issue.fromLocationsInfo?.name && (
                                          <>
                                            <span className="text-primary" role="button" style={{ cursor: 'pointer' }}
                                              onClick={(e) => { e.stopPropagation(); openPlanningLocation(issue.fromLocationsInfo.name); }}>
                                              {issue.fromLocationsInfo.name}
                                            </span>
                                            {' → '}
                                          </>
                                        )}
                                        {issue.locationsInfo?.name ? (
                                          <span className="text-primary" role="button" style={{ cursor: 'pointer' }}
                                            onClick={(e) => { e.stopPropagation(); openPlanningLocation(issue.locationsInfo.name); }}>
                                            {issue.locationsInfo.name}
                                          </span>
                                        ) : t("shared.n_a", "N/A")}
                                      </td>
                                      <td className="py-3">
                                        <Badge
                                          className={`badge ${getStatusBadgeClass(
                                            issue?.status
                                          )} rounded-pill px-3 py-1`}
                                        >
                                          {t(`shared.${(issue?.status || '').toLowerCase().replace(/\s+/g, '_')}`, formatEnumName(issue?.status))}
                                        </Badge>
                                        {issue?.startDate && new Date(issue.startDate) > new Date() && (
                                          <Badge color="warning" className="rounded-pill px-2 py-1 ms-1">
                                            {t("business.planning.scheduled", "Scheduled")}
                                          </Badge>
                                        )}
                                      </td>
                                      <td className="py-3">
                                        <span className="fw-medium">
                                          {issue.reason || t("shared.n_a", "N/A")}
                                        </span>
                                      </td>
                                    </motion.tr>
                                  )
                                )}
                              </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-search text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">
                            {t("business.asset_detail.no_planning_entries_found", "No planning entries found")}
                          </h5>
                          <p className="text-muted mb-0">
                            {t("business.asset_detail.no_planning_entries", "This asset has no planning entries")}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Service Contracts Tab */}
            <TabPane tabId="warranties">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="d-flex align-items-center">
                        <div className="info-icon me-3">
                          <div
                            className="bg-success bg-opacity-50 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="bx bx-shield-check text-success fs-5"></i>
                          </div>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("shared.service_contracts", "Service Contracts")}</h5>
                          <p className="text-muted small mb-0">
                            {t("business.asset_detail.view_contracts", "View service contracts")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <Row className="mb-4">
                      <Col md={6}>
                        <InputGroup>
                          <span className="input-group-text bg-transparent border-end-0">
                            <i className="bx bx-search text-muted"></i>
                          </span>
                          <Input
                            type="text"
                            placeholder={t("shared.search_contracts_by_vendor_contract_numb", "Search contracts by vendor, contract number, or notes...")}
                            value={warrantySearchTerm}
                            onChange={(e) =>
                              setWarrantySearchTerm(e.target.value)
                            }
                            className="border-start-0 ps-0"
                          />
                        </InputGroup>
                      </Col>
                    </Row>
                    <div
                      style={{
                        maxHeight: 'calc(90vh - 400px)',
                        overflow: 'auto',
                      }}
                    >
                      {loadingWarranties ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" size="lg" />
                          <p className="text-muted mt-3">
                            {t("shared.loading_contracts_message", "Loading service contracts...")}
                          </p>
                        </div>
                      ) : filteredWarranties.length > 0 ? (
                        <div className="warranty-grid">
                          <AnimatePresence>
                            {filteredWarranties.map((warranty, index) => (
                              <motion.div
                                key={warranty?.id || index}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{
                                  duration: 0.3,
                                  delay: index * 0.1,
                                }}
                                className="mb-4"
                              >
                                <Card className="border-0 shadow-sm h-100">
                                  <CardBody className="p-4">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                      <div className="d-flex align-items-center">
                                        <div className="avatar-xs bg-success bg-opacity-10 rounded-circle me-2 d-flex align-items-center justify-content-center">
                                          <i className="bx bx-shield-check text-success"></i>
                                        </div>
                                        <div>
                                          <h6 className="mb-1 fw-bold">
                                            {t("business.asset_detail.service_contract_number", "Service Contract #")}{index + 1}
                                          </h6>
                                          <Badge
                                            color={getWarrantyStatusBadge(
                                              warranty
                                            )}
                                            className="px-2 py-1"
                                            style={{ fontSize: '10px' }}
                                          >
                                            {warranty?.overallStatus
                                              ? t(`shared.status_${warranty.overallStatus.toLowerCase().replace(/\s+/g, '_')}`, warranty.overallStatus)
                                              : t("shared.unknown", "Unknown")}
                                          </Badge>
                                        </div>
                                      </div>
                                    </div>
                                    <Row className="g-3">
                                      <Col md={6}>
                                        <div className="info-section bg-light rounded p-3">
                                          <h6 className="text-primary mb-2 fw-semibold">
                                            <i className="bx bx-shield me-1"></i>
                                            {t("shared.manufacturer_warranty", "Manufacturer Warranty")}
                                          </h6>
                                          <div className="info-item mb-2">
                                            <small className="text-muted">
                                              {t("shared.status", "Status")}:
                                            </small>
                                            <div className="fw-medium">
                                              {warranty?.hasManufacturerWarranty ? (
                                                <Badge
                                                  color={
                                                    warranty?.manufacturerWarrantyStatus ===
                                                    'Active'
                                                      ? 'success'
                                                      : warranty?.manufacturerWarrantyStatus ===
                                                        'Expired'
                                                      ? 'danger'
                                                      : 'secondary'
                                                  }
                                                  className="ms-1"
                                                >
                                                  {warranty?.manufacturerWarrantyStatus
                                                    ? t(`shared.${warranty.manufacturerWarrantyStatus.toLowerCase()}`, warranty.manufacturerWarrantyStatus)
                                                    : t("shared.active", "Active")}
                                                </Badge>
                                              ) : (
                                                <Badge
                                                  color="secondary"
                                                  className="ms-1"
                                                >
                                                  {t("shared.not_available", "Not Available")}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          {warranty?.hasManufacturerWarranty && (
                                            <>
                                              <div className="info-item mb-2">
                                                <small className="text-muted">
                                                  {t("shared.period", "Period")}:
                                                </small>
                                                <div className="fw-medium small">
                                                  {warranty?.manufacturerWarrantyStartDate
                                                    ? new Date(
                                                        warranty.manufacturerWarrantyStartDate
                                                      ).toLocaleDateString('en-GB')
                                                    : t("shared.n_a", "N/A")}{' '}
                                                  -{' '}
                                                  {warranty?.manufacturerWarrantyEndDate
                                                    ? new Date(
                                                        warranty.manufacturerWarrantyEndDate
                                                      ).toLocaleDateString('en-GB')
                                                    : t("shared.n_a", "N/A")}
                                                </div>
                                              </div>
                                              <div className="info-item">
                                                <small className="text-muted">
                                                  {t("shared.days_remaining", "Days Remaining")}:
                                                </small>
                                                <div className="fw-medium">
                                                  {warranty?.manufacturerWarrantyDaysRemaining ||
                                                    t("shared.n_a", "N/A")}
                                                </div>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <div className="info-section bg-light rounded p-3">
                                          <h6 className="text-info mb-2 fw-semibold">
                                            <i className="bx bx-shield-plus me-1"></i>
                                            {t("shared.warranty_extension", "Warranty Extension")}
                                          </h6>
                                          <div className="info-item mb-2">
                                            <small className="text-muted">
                                              {t("shared.status", "Status")}:
                                            </small>
                                            <div className="fw-medium">
                                              {warranty?.hasWarrantyExtension ? (
                                                <Badge
                                                  color={
                                                    warranty?.warrantyExtensionStatus ===
                                                    'Active'
                                                      ? 'success'
                                                      : warranty?.warrantyExtensionStatus ===
                                                        'Expired'
                                                      ? 'danger'
                                                      : 'secondary'
                                                  }
                                                  className="ms-1"
                                                >
                                                  {warranty?.warrantyExtensionStatus
                                                    ? t(`shared.${warranty.warrantyExtensionStatus.toLowerCase()}`, warranty.warrantyExtensionStatus)
                                                    : t("shared.active", "Active")}
                                                </Badge>
                                              ) : (
                                                <Badge
                                                  color="secondary"
                                                  className="ms-1"
                                                >
                                                  {t("shared.not_available", "Not Available")}
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                          {warranty?.hasWarrantyExtension && (
                                            <>
                                              <div className="info-item mb-2">
                                                <small className="text-muted">
                                                  {t("shared.period", "Period")}:
                                                </small>
                                                <div className="fw-medium small">
                                                  {warranty?.warrantyExtensionStartDate
                                                    ? new Date(
                                                        warranty.warrantyExtensionStartDate
                                                      ).toLocaleDateString('en-GB')
                                                    : t("shared.n_a", "N/A")}{' '}
                                                  -{' '}
                                                  {warranty?.warrantyExtensionEndDate
                                                    ? new Date(
                                                        warranty.warrantyExtensionEndDate
                                                      ).toLocaleDateString('en-GB')
                                                    : t("shared.n_a", "N/A")}
                                                </div>
                                              </div>
                                              <div className="info-item">
                                                <small className="text-muted">
                                                  {t("shared.days_remaining", "Days Remaining")}:
                                                </small>
                                                <div className="fw-medium">
                                                  {warranty?.warrantyExtensionDaysRemaining ||
                                                    t("shared.n_a", "N/A")}
                                                </div>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </Col>
                                      <Col md={12}>
                                        <div className="info-section bg-light rounded p-3">
                                          <h6 className="text-warning mb-2 fw-semibold">
                                            <i className="bx bx-wrench me-1"></i>
                                            {t("shared.maintenance_contract", "Maintenance Contract")}
                                          </h6>
                                          <Row className="g-2">
                                            <Col md={3}>
                                              <div className="info-item">
                                                <small className="text-muted">
                                                  {t("shared.contract_type", "Contract Type")}:
                                                </small>
                                                <div className="fw-medium">
                                                  {(() => {
                                                    const type = warranty?.maintenanceContractType || '';
                                                    if (type === 'NO_CONTRACT') return t("shared.maintenance_contract_types_no_contract", "No Contract");
                                                    const typeKey = type.split(' ').map(word => word[0]?.toLowerCase()).filter(char => char && char.match(/[a-z]/i)).join('');
                                                    return t(`shared.maintenance_contract_types_${typeKey}`, type) || t("shared.n_a", "N/A");
                                                  })()}
                                                </div>
                                              </div>
                                            </Col>
                                            {warranty?.maintenanceContractType &&
                                              warranty?.maintenanceContractType !==
                                                'NO_CONTRACT' && (
                                                <>
                                                  <Col md={3}>
                                                    <div className="info-item">
                                                      <small className="text-muted">
                                                        {t("shared.vendor", "Vendor")}:
                                                      </small>
                                                      <div className="fw-medium">
                                                        {warranty?.maintenanceVendorName ||
                                                          t("shared.n_a", "N/A")}
                                                      </div>
                                                    </div>
                                                  </Col>
                                                  <Col md={3}>
                                                    <div className="info-item">
                                                      <small className="text-muted">
                                                        {t("shared.contract_number", "Contract #:")}
                                                      </small>
                                                      <div className="fw-medium">
                                                        {warranty?.contractNumber ||
                                                          t("shared.n_a", "N/A")}
                                                      </div>
                                                    </div>
                                                  </Col>
                                                  <Col md={3}>
                                                    <div className="info-item">
                                                      <small className="text-muted">
                                                        {t("shared.value", "Value:")}
                                                      </small>
                                                      <div className="fw-medium">
                                                        {warranty?.contractValueFormatted ||
                                                          t("shared.n_a", "N/A")}
                                                      </div>
                                                    </div>
                                                  </Col>
                                                  <Col md={6}>
                                                    <div className="info-item">
                                                      <small className="text-muted">
                                                        {t("shared.contract_period", "Contract Period:")}
                                                      </small>
                                                      <div className="fw-medium small">
                                                        {warranty?.contractStartDate
                                                          ? new Date(
                                                              warranty.contractStartDate
                                                            ).toLocaleDateString('en-GB')
                                                          : t("shared.n_a", "N/A")}{' '}
                                                        -{' '}
                                                        {warranty?.contractEndDate
                                                          ? new Date(
                                                              warranty.contractEndDate
                                                            ).toLocaleDateString('en-GB')
                                                          : t("shared.n_a", "N/A")}
                                                      </div>
                                                    </div>
                                                  </Col>
                                                  <Col md={6}>
                                                    <div className="info-item">
                                                      <small className="text-muted">
                                                        {t("shared.contract_status", "Contract Status:")}
                                                      </small>
                                                      <div className="fw-medium">
                                                        <Badge
                                                          color={
                                                            warranty?.contractStatus ===
                                                            'Active'
                                                              ? 'success'
                                                              : warranty?.contractStatus ===
                                                                'Expired'
                                                              ? 'danger'
                                                              : 'secondary'
                                                          }
                                                          className="ms-1"
                                                        >
                                                          {warranty?.contractStatus
                                                            ? t(`shared.${warranty.contractStatus.toLowerCase()}`, warranty.contractStatus)
                                                            : t("shared.n_a", "N/A")}
                                                        </Badge>
                                                      </div>
                                                    </div>
                                                  </Col>
                                                </>
                                              )}
                                          </Row>
                                        </div>
                                      </Col>
                                      {warranty?.notes && (
                                        <Col md={12}>
                                          <div className="info-section bg-light rounded p-3">
                                            <h6 className="text-secondary mb-2 fw-semibold">
                                              <i className="bx bx-note me-1"></i>
                                              {t("shared.notes", "Notes")}
                                            </h6>
                                            <div
                                              className="text-wrap"
                                              style={{ lineHeight: '1.5' }}
                                            >
                                              {warranty.notes}
                                            </div>
                                          </div>
                                        </Col>
                                      )}
                                    </Row>
                                  </CardBody>
                                </Card>
                              </motion.div>
                            ))}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-shield-check text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">
                            {t("shared.no_service_contracts_found", "No service contracts found")}
                          </h5>
                          <p className="text-muted mb-0">
                            {warrantySearchTerm
                              ? t("business.asset_detail.no_contracts_match", "No service contracts match your search criteria")
                              : t("business.asset_detail.asset_no_contracts", "No service contracts have been added for this asset")}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Sub Assets Tab */}
            <TabPane tabId="subAssets">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="d-flex align-items-center">
                        <div className="info-icon me-3">
                          <div
                            className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="bx bx-sitemap text-info fs-5"></i>
                          </div>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("shared.sub_assets", "Sub Assets")}</h5>
                          <p className="text-muted small mb-0">
                            {t("business.asset_detail.view_sub_assets", "View sub assets under this parent asset")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        maxHeight: 'calc(90vh - 400px)',
                        overflow: 'auto',
                      }}
                    >
                      {loadingSubAssets ? (
                        <div className="text-center py-5">
                          <Spinner color="primary" size="lg" />
                          <p className="text-muted mt-3">
                            {t("shared.loading_sub_assets", "Loading sub assets...")}
                          </p>
                        </div>
                      ) : subAssets.length > 0 ? (
                        <div className="table-responsive">
                          <Table hover className="mb-0 modern-table">
                            <thead className="table-light">
                              <tr>
                                <th className="border-0 py-3">#</th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-package me-1 text-muted"></i>
                                  {t("shared.name", "Name")}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-barcode me-1 text-muted"></i>
                                  {t("shared.asset_id", "Asset ID")}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-hash me-1 text-muted"></i>
                                  {t("shared.serial_number", "Serial Number")}
                                </th>
                                <th className="border-0 py-3">
                                  <i className="bx bx-flag me-1 text-muted"></i>
                                  {t("shared.status", "Status")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <AnimatePresence>
                                {subAssets.map((subAsset, index) => (
                                  <motion.tr
                                    key={subAsset.id || index}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: index * 0.05,
                                    }}
                                    whileHover={{ backgroundColor: '#f8f9fa' }}
                                  >
                                    <td className="py-3">{index + 1}</td>
                                    <td className="py-3">
                                      <div className="d-flex align-items-center">
                                        <div className="avatar-xs bg-light rounded me-2 d-flex align-items-center justify-content-center">
                                          <i className="bx bx-package text-primary"></i>
                                        </div>
                                        <span className="fw-medium text-primary">
                                          {subAsset.assetName}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="py-3">
                                      {subAsset.assetNumber || '-'}
                                    </td>
                                    <td className="py-3">
                                      {subAsset.serialNumber || '-'}
                                    </td>
                                    <td className="py-3">
                                      <Badge
                                        color={
                                          subAsset.assetStatus === 'ONLINE' || subAsset.assetStatus === 'ACTIVE'
                                            ? 'success'
                                            : subAsset.assetStatus === 'OFFLINE' || subAsset.assetStatus === 'INACTIVE'
                                            ? 'danger'
                                            : subAsset.assetStatus ===
                                              'MAINTENANCE'
                                            ? 'warning'
                                            : 'secondary'
                                        }
                                        className="px-2 py-1"
                                        style={{ fontSize: '10px' }}
                                      >
                                        {formatRoleName(subAsset.assetStatus, t) || t("shared.n_a", "N/A")}
                                        {subAsset.assetStatus === 'OFFLINE' && subAsset.downtimeReasonName ? ` (${t(`shared.${subAsset.downtimeReasonName.toLowerCase().replace(/\s+/g, '_')}`, subAsset.downtimeReasonName)})` : ''}
                                      </Badge>
                                    </td>
                                  </motion.tr>
                                ))}
                              </AnimatePresence>
                            </tbody>
                          </Table>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="avatar-lg bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center">
                            <i className="bx bx-sitemap text-muted fs-1"></i>
                          </div>
                          <h5 className="text-muted">{t("shared.no_sub_assets_found", "No sub assets found")}</h5>
                          <p className="text-muted mb-0">
                            {t("shared.no_sub_assets_have_been_added_for_this_a", "No sub assets have been added for this asset")}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Activities Tab */}
            <TabPane tabId="activities">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div
                          className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FeatherIcon
                            icon="activity"
                            size={20}
                            className="text-info"
                          />
                        </div>
                      </div>
                      <div>
                        <h5 className="mb-1 fw-bold">{t("shared.activity_timeline", "Activity Timeline")}</h5>
                        <p className="text-muted small mb-0">
                          {t("shared.complete_history_of_asset_activities", "Complete history of asset activities")}
                        </p>
                      </div>
                    </div>
                    {loadingActivities ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">
                          {t("shared.loading_activity_log", "Loading activity log...")}
                        </p>
                      </div>
                    ) : activities.length > 0 ? (
                      <div
                        className="activity-timeline"
                        style={{ maxHeight: '400px', overflowY: 'auto' }}
                      >
                        {activities.map((activity, index) => (
                          <motion.div
                            key={activity?.id || index}
                            className="timeline-item d-flex mb-4"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                          >
                            <div className="timeline-marker me-3">
                              <div
                                className="timeline-dot bg-primary rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  minWidth: '32px',
                                }}
                              >
                                <FeatherIcon
                                  icon="clock"
                                  size={14}
                                  className="text-white"
                                />
                              </div>
                              {index < activities.length - 1 && (
                                <div
                                  className="timeline-line bg-light"
                                  style={{
                                    width: '2px',
                                    height: '40px',
                                    marginLeft: '15px',
                                    marginTop: '4px',
                                  }}
                                ></div>
                              )}
                            </div>
                            <div className="timeline-content flex-grow-1">
                              <div className="timeline-card bg-light rounded p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <div className="fw-semibold text-dark">
                                      {activity.userName}
                                    </div>
                                    <div className="text-muted">
                                      {translateActivity(activity, t)}
                                    </div>
                                  </div>
                                  <Badge
                                    color="light"
                                    className="text-muted small"
                                  >
                                    <FeatherIcon
                                      icon="calendar"
                                      size={12}
                                      className="me-1"
                                    />
                                    {formatCreatedDate(activity?.createdAt)}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div
                          className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                          style={{
                            width: '64px',
                            height: '64px',
                            backgroundColor: '#f8f9fa',
                          }}
                        >
                          <FeatherIcon
                            icon="clock"
                            size={24}
                            className="text-muted"
                          />
                        </div>
                        <h5 className="text-muted">{t("shared.no_activity_recorded", "No activity recorded")}</h5>
                        <p className="text-muted mb-0">
                          {t("shared.activity_will_appear_here_as_the_asset_p", "Activity will appear here as the asset progresses")}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Spare Parts Tab */}
            <TabPane tabId="spareParts">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <div className="info-icon me-3">
                        <div
                          className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: '40px', height: '40px' }}
                        >
                          <FeatherIcon
                            icon="tool"
                            size={20}
                            className="text-primary"
                          />
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-1 fw-bold">{t("shared.spare_parts_usage", "Spare Parts Usage")}</h5>
                        <p className="text-muted small mb-0">
                          {t("business.asset_detail.spare_parts_used", "Spare parts used for this asset")}
                        </p>
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={handleOpenAttachSparePart}
                      >
                        <FeatherIcon icon="plus" size={14} className="me-1" />
                        {t("business.asset_detail.attach_spare_part", "Attach Spare Part")}
                      </Button>
                    </div>
                    {loadingSparePartsConsumables ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">
                          {t("shared.loading_spare_parts", "Loading spare parts...")}
                        </p>
                      </div>
                    ) : sparePartsUsage.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th>#</th>
                              <th>{t("shared.spare_part_name", "Spare Part Name")}</th>
                              <th>{t("shared.reference_number", "Reference Number")}</th>
                              <th>{t("shared.quantity", "Quantity")}</th>
                              <th>{t("shared.status", "Status")}</th>
                              <th>{t("shared.purchase_date", "Purchase Date")}</th>
                              <th>{t("shared.receipt_date", "Receipt Date")}</th>
                              <th>{t("shared.batch_number", "Batch Number")}</th>
                              <th>{t("shared.expiry_date", "Expiry Date")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            <AnimatePresence>
                              {sparePartsUsage.map((sparePart, index) => (
                                <motion.tr
                                  key={sparePart.id}
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  transition={{
                                    duration: 0.2,
                                    delay: index * 0.05,
                                  }}
                                >
                                  <td>{index + 1}</td>
                                  <td className="fw-medium">
                                    {sparePart.sparePartsResponse?.name ||
                                      t("shared.n_a", "N/A")}
                                  </td>
                                  <td>
                                    <code className="text-primary">
                                      {sparePart.referenceNumber || t("shared.n_a", "N/A")}
                                    </code>
                                  </td>
                                  <td>
                                    <Badge color="info" pill>
                                      {sparePart.quantity}
                                    </Badge>
                                  </td>
                                  <td>
                                    <Badge
                                      color={
                                        sparePart.status === 'APPROVED'
                                          ? 'success'
                                          : sparePart.status === 'PENDING'
                                          ? 'warning'
                                          : 'secondary'
                                      }
                                      pill
                                    >
                                      {sparePart.status}
                                    </Badge>
                                  </td>
                                  <td>{sparePart.purchaseDate || t("shared.n_a", "N/A")}</td>
                                  <td>{sparePart.receiptDate || t("shared.n_a", "N/A")}</td>
                                  <td>{sparePart.batchNumber || '-'}</td>
                                  <td>{sparePart.expiryDate || t("shared.n_a", "N/A")}</td>
                                </motion.tr>
                              ))}
                            </AnimatePresence>
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div
                          className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                          style={{
                            width: '64px',
                            height: '64px',
                            backgroundColor: '#f8f9fa',
                          }}
                        >
                          <FeatherIcon
                            icon="tool"
                            size={24}
                            className="text-muted"
                          />
                        </div>
                        <h5 className="text-muted">{t("shared.no_spare_parts_usage", "No spare parts usage")}</h5>
                        <p className="text-muted mb-0">
                          {t("shared.no_spare_parts_have_been_used_for_this_a", "No spare parts have been used for this asset")}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Meters Tab */}
            <TabPane tabId="meters">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div className="d-flex align-items-center">
                        <div className="info-icon me-3">
                          <div
                            className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <FeatherIcon
                              icon="activity"
                              size={20}
                              className="text-primary"
                            />
                          </div>
                        </div>
                        <div>
                          <h5 className="mb-1 fw-bold">{t("meters.title", "Meters")}</h5>
                          <p className="text-muted small mb-0">
                            {t("meters.asset_meters_desc", "Meters tracking usage and conditions for this asset")}
                          </p>
                        </div>
                      </div>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => navigate(`/meters?assetId=${asset?.id}`)}
                      >
                        <FeatherIcon icon="plus" size={14} className="me-1" />
                        {t("meters.create_meter", "Create Meter")}
                      </Button>
                    </div>
                    {loadingMeters ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" size="lg" />
                        <p className="text-muted mt-3">
                          {t("shared.loading", "Loading...")}
                        </p>
                      </div>
                    ) : assetMeters.length > 0 ? (
                      <div className="table-responsive">
                        <Table hover className="mb-0">
                          <thead className="bg-light">
                            <tr>
                              <th>{t("meters.name", "Name")}</th>
                              <th>{t("meters.measurement_unit", "Unit")}</th>
                              <th>{t("meters.last_reading", "Last Reading")}</th>
                              <th>{t("meters.status", "Status")}</th>
                              <th>{t("meters.last_read_date", "Last Read Date")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assetMeters.map((meter) => (
                              <tr
                                key={meter.id}
                              >
                                <td className="fw-medium">
                                  <span
                                    className="text-primary cursor-pointer hover-underline"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setDetailMeterId(meter.id)}
                                  >
                                    {meter.name}
                                  </span>
                                </td>
                                <td>{meter.measurementUnit ? t(`meters.unit_${meter.measurementUnit.toLowerCase().replace(/[\s/²]+/g, '_')}`, meter.measurementUnit) : '-'}</td>
                                <td>{meter.lastReadingValue != null ? meter.lastReadingValue : '-'}</td>
                                <td>
                                  <Badge color={METER_STATUS_COLORS[meter.status] || 'secondary'}>
                                    {t(`meters.status_${meter.status?.toLowerCase()}`, meter.status)}
                                  </Badge>
                                </td>
                                <td>
                                  {meter.lastReadingAt
                                    ? new Date(meter.lastReadingAt).toLocaleDateString('en-GB')
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <FeatherIcon icon="activity" size={48} className="text-muted mb-3" />
                        <h5 className="text-muted">{t("meters.no_meters", "No meters found")}</h5>
                        <p className="text-muted mb-0">
                          {t("meters.no_meters_for_asset", "No meters have been configured for this asset")}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </TabPane>

            {/* Downtime Tab: per-asset Total Downtime + Downtime Cost */}
            <TabPane tabId="downtime">
              <div className="p-4">
                {asset?.organization?.id && asset?.id ? (
                  <>
                    <div className="mb-4">
                      <TotalDowntimeWidget
                        organizationId={asset.organization.id}
                        dateRange={downtimeDateRange}
                        assetId={asset.id}
                      />
                    </div>
                    <div>
                      <DowntimeCostWidget
                        organizationId={asset.organization.id}
                        dateRange={downtimeDateRange}
                        assetId={asset.id}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    {t('shared.loading', 'Loading...')}
                  </div>
                )}
              </div>
            </TabPane>

            {/* Cost Tab: lifetime TCO + period-filterable maintenance cost rollup. */}
            <TabPane tabId="cost">
              <div className="p-4">
                {asset?.id ? (
                  <Row className="g-3">
                    <Col md={12}>
                      <AssetTCOPanel assetId={asset.id} />
                    </Col>
                    <Col md={12}>
                      <AssetCostRollupCard assetId={asset.id} />
                    </Col>
                  </Row>
                ) : (
                  <div className="text-center text-muted py-5">
                    {t('shared.loading', 'Loading...')}
                  </div>
                )}
              </div>
            </TabPane>
          </TabContent>
        </Card>

        {/* Image Modal */}
        <AnimatePresence>
          {showImageModal && asset?.assetImageUrls?.length > 0 && (
            <Modal
              isOpen={showImageModal}
              toggle={closeImageModal}
              size="lg"
              centered
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
              >
                <div className="modal-header">
                  <h5 className="modal-title">
                    {t("shared.asset_image", "Asset Image")} {selectedImageIndex + 1} {t("shared.of", "of")}{' '}
                    {asset.assetImageUrls.length}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeImageModal}
                  ></button>
                </div>
                <div className="modal-body text-center p-0">
                  <img
                    src={asset.assetImageUrls[selectedImageIndex]}
                    alt={`Asset image ${selectedImageIndex + 1}`}
                    className="img-fluid"
                    style={{ maxHeight: '70vh', objectFit: 'contain' }}
                  />
                </div>
                <div className="modal-footer justify-content-center">
                  <Button
                    color="outline-secondary"
                    onClick={() =>
                      setSelectedImageIndex((prev) =>
                        prev > 0 ? prev - 1 : asset.assetImageUrls.length - 1
                      )
                    }
                    disabled={asset.assetImageUrls.length <= 1}
                  >
                    <FeatherIcon icon="chevron-left" size={18} />
                  </Button>
                  <span className="mx-3">
                    {selectedImageIndex + 1} / {asset.assetImageUrls.length}
                  </span>
                  <Button
                    color="outline-secondary"
                    onClick={() =>
                      setSelectedImageIndex((prev) =>
                        prev < asset.assetImageUrls.length - 1 ? prev + 1 : 0
                      )
                    }
                    disabled={asset.assetImageUrls.length <= 1}
                  >
                    <FeatherIcon icon="chevron-right" size={18} />
                  </Button>
                </div>
              </motion.div>
            </Modal>
          )}
        </AnimatePresence>

        {/* Meter Detail Modal */}
        <MeterDetailModal
          isOpen={!!detailMeterId}
          toggle={() => setDetailMeterId(null)}
          meterId={detailMeterId}
          onUpdate={fetchAssetMeters}
        />

        {/* Status Change Modal */}
        <AssetStatusChangeModal
          isOpen={showStatusModal}
          toggle={() => setShowStatusModal(false)}
          asset={asset}
          onSuccess={() => fetchAssetByUuid()}
        />

        {/* Asset Transfer Modal */}
        <AssetTransferModal
          isOpen={showTransferModal}
          toggle={() => setShowTransferModal(false)}
          asset={asset}
          onSuccess={() => fetchAssetByUuid()}
        />

        {/* Transfer details — same modal as the Planning page, opened from a Planning row */}
        <TransferDetailsModal
          isOpen={transferDetails.isOpen}
          toggle={() => setTransferDetails({ isOpen: false, transfer: null })}
          transfer={transferDetails.transfer}
          onUpdated={(updated) => {
            if (updated) setTransferDetails((prev) => ({ ...prev, transfer: updated }));
            fetchAssetByUuid();
          }}
        />

        {/* Location details for a clicked Planning-row location */}
        <LocationModal
          isOpen={planningLocationModal.isOpen}
          toggle={() => setPlanningLocationModal((prev) => ({ ...prev, isOpen: false }))}
          title={planningLocationModal.title}
          mainData={planningLocationModal.mainData}
          relatedData={planningLocationModal.relatedData}
          loading={planningLocationModal.loading}
          formatCreatedDate={formatCreatedDate}
          locationData={planningLocationModal.locationData}
        />

        {/* Attach Spare Part Modal */}
        <Modal
          isOpen={showAttachSparePartModal}
          toggle={() => setShowAttachSparePartModal(false)}
          centered
        >
          <div className="modal-header">
            <h5 className="modal-title">
              {t("business.asset_detail.attach_spare_part", "Attach Spare Part")}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowAttachSparePartModal(false)}
            />
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label">
                {t("business.asset_detail.select_spare_part", "Select Spare Part")}
              </label>
              <Input
                type="select"
                value={selectedSparePartId}
                onChange={(e) => setSelectedSparePartId(e.target.value)}
              >
                <option value="">
                  {t("business.asset_detail.select_spare_part_placeholder", "-- Select a spare part --")}
                </option>
                {availableSpareParts
                  .filter((sp) => sp.quantity > 0)
                  .map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.name} ({t("shared.available", "Available")}: {sp.available ?? sp.quantity})
                    </option>
                  ))}
              </Input>
            </div>
            <div className="mb-3">
              <label className="form-label">
                {t("shared.quantity", "Quantity")}
              </label>
              <Input
                type="number"
                min={1}
                max={
                  availableSpareParts.find(
                    (sp) => String(sp.id) === String(selectedSparePartId)
                  )?.available ??
                  availableSpareParts.find(
                    (sp) => String(sp.id) === String(selectedSparePartId)
                  )?.quantity ??
                  999
                }
                value={attachQuantity}
                onChange={(e) => setAttachQuantity(Number(e.target.value))}
              />
            </div>
          </div>
          <div className="modal-footer">
            <Button
              color="secondary"
              onClick={() => setShowAttachSparePartModal(false)}
            >
              {t("shared.cancel", "Cancel")}
            </Button>
            <Button
              color="primary"
              disabled={!selectedSparePartId || attachQuantity < 1 || attachLoading}
              onClick={handleAttachSparePart}
            >
              {attachLoading ? (
                <Spinner size="sm" className="me-1" />
              ) : null}
              {t("business.asset_detail.attach", "Attach")}
            </Button>
          </div>
        </Modal>
      </Container>
    </div>
  );
};

export default AssetDetail;
